import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { inspMethodApi, F11G } from '../../../services/inspMethodApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useInspMethodForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F11G[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [descCn, setDescCn] = useState('');
  const [descEn, setDescEn] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await inspMethodApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setCode(current.f11g_01); setDescCn(current.f11g_11); setDescEn(current.f11g_12); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setCode(''); setDescCn(''); setDescEn(''); };
  const startEdit = () => {
    if (!current) return; setMode('edit');
    setCode(current.f11g_01); setDescCn(current.f11g_11); setDescEn(current.f11g_12);
  };
  const cancel = () => {
    setMode('browse');
    if (current) { setCode(current.f11g_01); setDescCn(current.f11g_11); setDescEn(current.f11g_12); }
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await inspMethodApi.create({ f11g_01: code, f11g_11: descCn, f11g_12: descEn });
        message.success('新增成功');
      } else {
        await inspMethodApi.update(current!.f11g_01, { f11g_11: descCn, f11g_12: descEn });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.f11g_01;
      const idx = data.findIndex(r => r.f11g_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await inspMethodApi.delete(current.f11g_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const exact = records.findIndex(r => r.f11g_01 === t);
    if (exact >= 0) { setCurrentIdx(exact); return; }
    const near = records.findIndex(r => r.f11g_01 >= t);
    if (near >= 0) { setCurrentIdx(near); message.info(`定位至最近的「${records[near].f11g_01}」`); }
    else { message.warning('查無此代號'); }
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    code, setCode, descCn, setDescCn, descEn, setDescEn,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
